import React, { useEffect, useState } from "react";
import { api, getReadableError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TX_TYPE_LABELS,
  TX_TYPE_OPTIONS,
  inputDateFromIso,
  isoFromInputDate,
  todayInputValue,
} from "@/lib/labels";

const EMPTY = {
  tx_type: "cambio_cheque",
  date: todayInputValue(),
  amount: "",
  notes: "",
};

export default function TransactionDialog({
  open,
  onOpenChange,
  clientId,
  tx,
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        tx
          ? {
              tx_type: tx.tx_type || "cambio_cheque",
              date: inputDateFromIso(tx.date),
              amount: tx.amount != null ? String(tx.amount) : "",
              notes: tx.notes || "",
            }
          : EMPTY
      );
    }
  }, [open, tx]);

  const isEdit = Boolean(tx?.id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amountNum = parseFloat(form.amount || "0");
      if (Number.isNaN(amountNum) || amountNum < 0) {
        toast.error("Monto inválido");
        setSaving(false);
        return;
      }
      const payload = {
        tx_type: form.tx_type,
        date: isoFromInputDate(form.date),
        amount: amountNum,
        notes: form.notes,
      };
      let data;
      if (isEdit) {
        const r = await api.patch(`/transactions/${tx.id}`, payload);
        data = r.data;
      } else {
        const r = await api.post(`/clients/${clientId}/transactions`, payload);
        data = r.data;
      }
      onSaved && onSaved(data);
      toast.success(isEdit ? "Movimiento actualizado" : "Movimiento registrado");
    } catch (err) {
      toast.error(getReadableError(err));
    } finally {
      setSaving(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border-slate-900 max-w-md"
        data-testid="tx-dialog"
      >
        <DialogHeader>
          <div className="label-eyebrow text-[#6B21A8]">
            {isEdit ? "·Editar movimiento" : "·Nuevo movimiento"}
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? TX_TYPE_LABELS[form.tx_type] : "Registrar movimiento"}
          </DialogTitle>
          <DialogDescription>
            Registra cambios de cheque, envíos, recargas o pagos del cliente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-eyebrow">Tipo</Label>
            <div className="mt-1.5">
              <Select
                value={form.tx_type}
                onValueChange={(v) => setForm((f) => ({ ...f, tx_type: v }))}
              >
                <SelectTrigger
                  className="rounded-none h-11 border-slate-300 focus:ring-2 focus:ring-[#6B21A8] focus:ring-offset-0"
                  data-testid="tx-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-slate-900">
                  {TX_TYPE_OPTIONS.map((k) => (
                    <SelectItem key={k} value={k} className="rounded-none">
                      {TX_TYPE_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Fecha</Label>
              <Input
                type="date"
                value={form.date}
                onChange={update("date")}
                className="mt-1.5 rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0 font-mono-tab"
                data-testid="tx-date-input"
                required
              />
            </div>
            <div>
              <Label className="label-eyebrow">Monto (USD)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={form.amount}
                onChange={update("amount")}
                className="mt-1.5 rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0 font-mono-tab"
                placeholder="0.00"
                data-testid="tx-amount-input"
                required
              />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={update("notes")}
              className="mt-1.5 rounded-none border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0 min-h-[72px]"
              placeholder="Comentarios, banco, # de cheque..."
              data-testid="tx-notes-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-none border-slate-300"
              data-testid="tx-cancel-button"
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-none bg-[#6B21A8] hover:bg-[#581C87] text-white font-bold uppercase tracking-wider btn-swap"
              data-testid="tx-submit-button"
            >
              {saving ? "Guardando..." : isEdit ? "Guardar" : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
